import React, { useState, useEffect, useCallback } from 'react';
import ImportUsers from './ImportUsers';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Box, Grid, Switch,
  FormControlLabel, Avatar, CircularProgress, useTheme, useMediaQuery,
  Card, CardContent, Divider, Chip
} from '@mui/material';
import { Edit, Delete, Add, CloudUpload, Search, Person, Phone, Badge } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';
import axios from 'axios';

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' }
  }
};
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const UsersManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    nationalityCode: '',
    medicalSystemNumber: '',
    profileImage: '',
    userRole: 'user',
    sex: '0',
    isActive: true,
    specialities: []
  });

  // State for ImportUsers dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Filter and search states
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      if (userRoleFilter !== "all") {
        params.append("userRole", userRoleFilter);
      }

      if (debouncedSearch.trim()) {
        params.append("searchQuery", debouncedSearch.trim());
      }

      const response = await api.get(
        `/users/users-list?${params.toString()}`
      );

      if (response.status !== 200) {
        throw new Error("خطا در دریافت لیست کاربران");
      }

      const result = response.data;
      setUsers(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotal(result.pagination?.total || 0);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching users:", err);
      snackbar("خطا در بارگیری کاربران");
    } finally {
      setLoading(false);
    }
  }, [page, userRoleFilter, debouncedSearch]);




  useEffect(() => {

    fetchUsers();
  }, [fetchUsers]);

  // const handleRoleChange = (role) => {
  //   setUserRole(role);
  //   setPage(1);
  // };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };


  const handleOpen = (user = null) => {
    if (user) {
      setCurrent(user);
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        nationalityCode: user.nationalityCode || '',
        medicalSystemNumber: user.medicalSystemNumber || '',
        profileImage: user.profileImage || '',
        userRole: user.userRole || 'user',
        sex: user.sex || '0',
        isActive: user.isActive !== undefined ? user.isActive : true,
        specialities: user.specialities || []
      });
    } else {
      setCurrent(null);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        nationalityCode: '',
        medicalSystemNumber: '',
        profileImage: '',
        userRole: 'user',
        sex: '0',
        isActive: true,
        specialities: []
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMetadataChange = (field, value) => {
    setFormData({ ...formData, metadata: { ...formData.metadata, [field]: value } });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/users/profile-image', fd);
      setFormData({ ...formData, profileImage: res.data.filename });
      successMessage('تصویر با موفقیت بارگذاری شد');
    } catch (err) {
      snackbar('خطا در بارگذاری تصویر');
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.phone || !formData.firstName) {
        snackbar('شماره تماس و نام الزامی هستند');
        return;
      }

      setSubmitting(true);

      if (current) {
        await api.put(`/users/${current._id}`, formData);
        successMessage('کاربر به‌روزرسانی شد');
      } else {
         await api.post(`/users`, formData);


        successMessage('کاربر ایجاد شد');
      }

      fetchUsers();
      handleClose();
    } catch (err) {
      console.error("handleSubmit", err.response, err)
      snackbar(err.response?.data?.message || 'خطا در ذخیره‌سازی');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) return;
    try {
      await api.delete(`/users/${id}`);
      successMessage('حذف شد');
      fetchUsers();
    } catch (err) {
      snackbar('خطا در حذف');
    }
  };

  const getImageUrl = (filename) => {
    if (!filename) return '';
    if (filename.startsWith('http')) return filename;
    return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/profile/${filename}`;
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, mt: isMobile ? 3 : 5 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between', 
        gap: 2,
        mb: 3, 
        mt: isMobile ? 6 : 5 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">مدیریت کاربران</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: isMobile ? 'column' : 'row' }}>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => handleOpen()}
            fullWidth={isMobile}
            sx={{ borderRadius: '12px', py: isMobile ? 1.5 : 1 }}
          >
            کاربر جدید
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<CloudUpload />} 
            onClick={() => setImportDialogOpen(true)}
            fullWidth={isMobile}
            sx={{ borderRadius: '12px', py: isMobile ? 1.5 : 1 }}
          >
            اکسل
          </Button>
        </Box>

        {/* Import Users Dialog */}
        <Dialog 
          open={importDialogOpen} 
          onClose={() => setImportDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 'bold' }}>افزودن کاربران از فایل اکسل</DialogTitle>
          <DialogContent dividers>
            <ImportUsers />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setImportDialogOpen(false)} sx={{ borderRadius: '8px' }}>بستن</Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 4, border: '1px solid #eee' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={8}>
            <TextField
              fullWidth
              placeholder="جستجو (نام، تماس، کد ملی)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
                sx: { borderRadius: '12px' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="نقش"
              value={userRoleFilter}
              onChange={(e) => {
                setUserRoleFilter(e.target.value);
                setPage(1);
              }}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              <MenuItem value="all">همه</MenuItem>
              <MenuItem value="user">کاربر</MenuItem>
              <MenuItem value="admin">ادمین</MenuItem>
              <MenuItem value="site-admin">ادمین سایت</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {isMobile ? (
        <Grid container spacing={2}>
          {loading ? (
             <Grid item xs={12} textAlign="center" py={4}><CircularProgress /></Grid>
          ) : (
            users.map((user) => (
              <Grid item xs={12} key={user._id}>
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                      <Avatar 
                        src={getImageUrl(user.profileImage)} 
                        variant="rounded" 
                        sx={{ width: 60, height: 60, borderRadius: 3 }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={user.userRole === 'admin' ? 'ادمین' : user.userRole === 'site-admin' ? 'ادمین سایت' : 'کاربر'}
                            color={user.userRole === 'admin' ? 'error' : user.userRole === 'site-admin' ? 'warning' : 'primary'}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: '6px', height: 20, fontSize: '10px' }}
                          />
                          <Chip 
                            label={user.isActive ? 'فعال' : 'غیرفعال'} 
                            color={user.isActive ? 'success' : 'default'} 
                            size="small" 
                            sx={{ borderRadius: '6px', height: 20, fontSize: '10px' }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                       <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Phone sx={{ fontSize: 14 }} /> {user.phone || '-'}
                       </Typography>
                       <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Badge sx={{ fontSize: 14 }} /> کدملی: {user.nationalityCode || '-'}
                       </Typography>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleOpen(user)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(user._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
          {!loading && users.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="textSecondary">کاربری یافت نشد</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell align="right">تصویر</TableCell>
                  <TableCell align="right">نام و خانوادگی</TableCell>
                  {!isTablet && <TableCell align="right">شماره تماس</TableCell>}
                  {!isTablet && <TableCell align="right">کد ملی</TableCell>}
                  <TableCell align="right">نقش</TableCell>
                  <TableCell align="right">وضعیت</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell align="right">
                      <Avatar
                        src={getImageUrl(user.profileImage)}
                        variant="rounded"
                        sx={{ width: 45, height: 45, borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    {!isTablet && <TableCell align="right">{user.phone || '-'}</TableCell>}
                    {!isTablet && <TableCell align="right">{user.nationalityCode || '-'}</TableCell>}
                    <TableCell align="right">
                      <Chip
                        label={user.userRole === 'admin' ? 'ادمین' : user.userRole === 'site-admin' ? 'ادمین سایت' : 'کاربر'}
                        color={user.userRole === 'admin' ? 'error' : user.userRole === 'site-admin' ? 'warning' : 'primary'}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: '6px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={user.isActive ? 'فعال' : 'غیرفعال'} 
                        color={user.isActive ? 'success' : 'default'} 
                        size="small" 
                        sx={{ borderRadius: '6px' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleOpen(user)} title="ویرایش">
                          <Edit fontSize="small" color="primary" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(user._id)} title="حذف">
                          <Delete fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                   <TableRow>
                     <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                       <Typography variant="body2" color="textSecondary">موردی یافت نشد</Typography>
                     </TableCell>
                   </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={4}>
          <Button
            variant="outlined"
            size="small"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
            sx={{ borderRadius: '8px' }}
          >
            قبلی
          </Button>
          <Typography variant="body2" sx={{ mx: 2 }}>
            صفحه {page} از {totalPages}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
            sx={{ borderRadius: '8px' }}
          >
            بعدی
          </Button>
        </Box>
      )}

      {/* User Form Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {current ? 'ویرایش اطلاعات کاربر' : 'تعریف کاربر جدید'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            {/* Profile Image Upload */}
            <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
              <Avatar
                src={getImageUrl(formData.profileImage)}
                sx={{ width: 100, height: 100, mb: 1.5, border: '2px solid #eee' }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                size="small"
                sx={{ borderRadius: '8px' }}
              >
                تغییر تصویر
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                />
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="نام"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="نام خانوادگی"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="شماره تماس (نام کاربری)"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  sx={textFieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="کد ملی"
                  name="nationalityCode"
                  value={formData.nationalityCode}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="شماره نظام پزشکی (اختیاری)"
                  name="medicalSystemNumber"
                  value={formData.medicalSystemNumber}
                  onChange={handleChange}
                  sx={textFieldStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="جنسیت"
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  sx={textFieldStyle}
                >
                  <MenuItem value="0">نامشخص</MenuItem>
                  <MenuItem value="1">زن</MenuItem>
                  <MenuItem value="2">مرد</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="نقش دسترسی"
                  name="userRole"
                  value={formData.userRole}
                  onChange={handleChange}
                  sx={textFieldStyle}
                >
                  <MenuItem value="user">کاربر عادی</MenuItem>
                  <MenuItem value="admin">مدیر (ادمین)</MenuItem>
                  <MenuItem value="site-admin">ادمین سایت (فقط پیام‌های سایت)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="حساب کاربری فعال باشد"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} sx={{ borderRadius: '8px' }}>لغو</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ borderRadius: '8px', px: 4 }}
          >
            {submitting ? <CircularProgress size={20} /> : 'ذخیره تغییرات'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
    

export default UsersManagement;
